import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import StepperControls from "@components/StepperControls/StepperControls";
import StepperTabPanel from "@components/StepperTabs/StepperTabPanel";
import StepperTabs from "@components/StepperTabs/StepperTabs";
import {
  STEPPER_TYPE,
  StepStatus,
  type StepperTabsHandle,
  type StepStatusValue,
} from "@components/StepperTabs/StepperTabs.types";
import { useStepStatuses } from "@components/StepperTabs/useStepStatuses";
import { useNavigationGuard } from "@context/NavigationGuardContext";
import { useNotification } from "@context/NotificationContext";
import type { FormContext } from "@custom-types/form-types";
import { shouldShowCargoOnboardSection } from "@features/vessel-call/cargoOnboard";
import { useCreateVesselCall } from "@features/vessel-call/create/hooks/useCreateVesselCall";
import {
  DOC_UPLOAD_FIELDS,
  mapVesselCallToFormState,
  type UploadItem,
} from "@features/vessel-call/create/mappers/mapVesselCallToFormState";
import { steps } from "@features/vessel-call/create/utils";
import { usePersistentForm } from "@hooks/usePersistentForm";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { getPorts } from "@services/referenceDataApi";
import type { Port, Vessel } from "@services/vessel/types";
import { getVesselCallById } from "@services/vesselCallApi";
import { getVesselByIdNoCompanyIdValidation } from "@services/vesselsApi";
import { useQuery } from "@tanstack/react-query";
import { hasFormData } from "@utils/form";
import { paths } from "@utils/navigation";
import type { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { VESSEL_CALL_STATUSES } from "../vessel-call-status/flow/utils";
import VesselDetails from "./components/VesselDetails/VesselDetails";
import { FORM_FIELDS, STEP_FIELDS } from "./constants";
import { getVesselCallFormSchema, type VesselCallFormData } from "./schema/vesselCallFormSchema";
import {
  defaultValues as emptyFormDefaults,
  PERSIST_KEY,
  useVesselCallFormStore,
  type VesselCallFormState,
} from "./store/useVesselCallForm.store";
import { validateVesselCertificates } from "./utils/validateCertificates";

export type VesselCallOutletContext = FormContext<VesselCallFormData> & {
  cancel: () => void;
  fetchedPorts: Port[];
  prefillFromId?: string;
  isFinalSubmission?: boolean;
  isRejectedFinalArrivalUpdate?: boolean;
  shouldEnforceFinalValidations?: boolean;
};

type VCFormData = VesselCallFormData & FieldValues;

const LANG_BASE = "vessel-call.create";
const { index } = paths.vesselCall;
const serviceVessel = "bff-vessel";
const serviceVesselCall = "bff-vessel-call";
const TEST_ID_BASE = "create-vessel-call";

const syncUploadStoreFromForm = (data: Partial<VesselCallFormState>) => {
  const uploadStore = useUploadStore.getState();

  DOC_UPLOAD_FIELDS.forEach((key) => {
    const files = (data as any)[key] as UploadItem[] | undefined;

    if (files && files.length > 0) {
      (uploadStore as any).setFilesForKey?.(key, files);
    } else {
      uploadStore.clearFilesForKey?.(key);
    }
  });
};

const CreateVesselCallContainer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { requestNavigation, hasActiveNavigationBlockers } = useNavigationGuard();
  const location = useLocation();
  const [search] = useSearchParams();
  const isShippingAgentUser = useAuthStore((state) => state.isShippingAgentUser);

  const [shouldShowCertWarning, setShouldShowCertWarning] = useState<boolean | undefined>(
    undefined,
  );
  const isFinalSubmission = useMemo(() => {
    const fromState = (location.state as any)?.finalSubmission;
    const fromQuery = search.get("finalSubmission");
    return Boolean(fromState ?? fromQuery === "true");
  }, [location.state, search]);

  const stepperTabsRef = useRef<StepperTabsHandle>(null);

  const { data: fetchedPorts = [] } = useQuery({
    queryKey: ["ports"],
    queryFn: () => getPorts(serviceVessel),
  });

  const uploadStore = useUploadStore.getState();

  const navigatePreserve = (to: string) => {
    navigate({ pathname: to, search: location.search }, { state: location.state });
  };

  const [touchedSteps, setTouchedSteps] = useState<Record<number, boolean>>(
    Object.keys(STEP_FIELDS).reduce(
      (acc, key) => {
        acc[Number(key)] = false;
        return acc;
      },
      {} as Record<number, boolean>,
    ),
  );

  const { setField, setAll, resetForm } = useVesselCallFormStore();

  const prefillFromId = useMemo(
    () => (location.state as any)?.prefillFromId || search.get("prefillFromId") || undefined,
    [location.state, search],
  );

  const prefillDoneRef = useRef(false);
  const skipMissingVesselGuardRef = useRef(false);
  const seededVesselMetaRef = useRef<string | null>(null);

  const [isPrefillHydrated, setIsPrefillHydrated] = useState(false);

  const { data: sourceCall } = useQuery({
    queryKey: ["vesselCall.prefill", serviceVesselCall, prefillFromId],
    queryFn: () => getVesselCallById(serviceVesselCall, prefillFromId as string),
    enabled: !!prefillFromId,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
  });

  const shouldEnforceFinalValidations = isFinalSubmission;
  const isRejectedFinalArrivalUpdate = Boolean(
    prefillFromId && sourceCall?.status === VESSEL_CALL_STATUSES.REJECTED_MARINE_OPERATOR,
  );

  const schema = useMemo(
    () => getVesselCallFormSchema(t, { isFinalSubmission: shouldEnforceFinalValidations }),
    [t, shouldEnforceFinalValidations],
  );
  const draftSchema = useMemo(() => getVesselCallFormSchema(t, { isDraft: true }), [t]);
  const formDefaults = emptyFormDefaults as VesselCallFormData;
  const storeValues: VesselCallFormData = useVesselCallFormStore.getState();

  const form = usePersistentForm<VesselCallFormData>({
    schema,
    defaultValues: formDefaults,
    storeValues: storeValues,
    setStoreValues: setAll,
    resetStore: resetForm,
    persistKey: PERSIST_KEY,
    dateKeys: [FORM_FIELDS.ETA, FORM_FIELDS.ETD],
  });

  const { reset, clear, clearErrors, watch, setError } = form;

  useEffect(() => {
    if (!prefillFromId && !isPrefillHydrated) {
      setIsPrefillHydrated(true);
    }
  }, [prefillFromId, isPrefillHydrated]);

  useEffect(() => {
    if (!prefillFromId || !sourceCall || prefillDoneRef.current) return;

    const hasVessel = !!sourceCall.vesselId;
    const isAllowedStatus = [
      "PRE_APPROVED",
      "REJECTED_MARINE_SUPERVISOR",
      "REJECTED_MARINE_OPERATOR",
      "ARRIVAL_DRAFT",
      "PRE_ARRIVAL_DRAFT",
    ].includes(sourceCall.status);
    if (!hasVessel || !isAllowedStatus) {
      prefillDoneRef.current = true;
      if (!isPrefillHydrated) setIsPrefillHydrated(true);
      return;
    }

    skipMissingVesselGuardRef.current = true;

    const patch = mapVesselCallToFormState(sourceCall) as Partial<VesselCallFormState>;
    (patch as any)[FORM_FIELDS.VESSEL_ID] = sourceCall.vesselId;

    const merged: VesselCallFormState = {
      ...(emptyFormDefaults as VesselCallFormState),
      ...patch,
    };

    setAll(merged);
    reset(merged as unknown as VesselCallFormData);
    syncUploadStoreFromForm(merged);

    form.setValue(FORM_FIELDS.VESSEL_ID, sourceCall.vesselId, {
      shouldDirty: false,
      shouldValidate: false,
    });

    prefillDoneRef.current = true;
    if (!isPrefillHydrated) setIsPrefillHydrated(true);

    setTimeout(() => {
      skipMissingVesselGuardRef.current = false;
    }, 0);
  }, [prefillFromId, sourceCall, reset, form, setAll, isPrefillHydrated]);

  useEffect(() => {
    const vesselIdFromState = (location.state as any)?.vessel?.id;
    if (vesselIdFromState) {
      updateFormData(FORM_FIELDS.VESSEL_ID, vesselIdFromState);
      form.setValue(FORM_FIELDS.VESSEL_ID, vesselIdFromState);
    }
  }, []);

  const vesselId = form.watch(FORM_FIELDS.VESSEL_ID);

  const { data: vesselDetails } = useQuery<Vessel>({
    queryKey: ["vessel", serviceVessel, vesselId],
    queryFn: () => getVesselByIdNoCompanyIdValidation(serviceVessel, vesselId as string),
    enabled: !!vesselId,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (prefillFromId) return;
    if (skipMissingVesselGuardRef.current) return;

    const currentVesselId = useVesselCallFormStore.getState().vesselId;
    if (!currentVesselId) {
      notify({
        title: t(`${LANG_BASE}.notifications.missing-vessel-selected-title`),
        description: t(`${LANG_BASE}.notifications.missing-vessel-selected-description`),
        type: "error",
      });
      navigate("/vessel-call/vessel-scan", { replace: true });
      return;
    }

    if (!vesselDetails) return;

    if (seededVesselMetaRef.current === currentVesselId) return;
    seededVesselMetaRef.current = currentVesselId;

    const imoNumberRaw = vesselDetails?.vesselImoNumber;
    const imoNumber = imoNumberRaw != null ? String(imoNumberRaw) : "";
    const vesselName = vesselDetails?.vesselName;
    const vesselType = vesselDetails?.vesselType?.vesselTypeDesc;
    const vesselNameAndType = [vesselName, vesselType].filter(Boolean).join(" ");

    updateFormData(FORM_FIELDS.IMO_NUMBER, imoNumber);
    updateFormData(FORM_FIELDS.NAME_AND_TYPE_OF_SHIP, vesselNameAndType);

    form.setValue(FORM_FIELDS.IMO_NUMBER, imoNumber as any, {
      shouldDirty: false,
      shouldValidate: false,
    });
    form.setValue(FORM_FIELDS.NAME_AND_TYPE_OF_SHIP, vesselNameAndType as any, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [vesselDetails, notify, navigate, form, t, prefillFromId]);

  const updateFormData: VesselCallOutletContext["updateFormData"] = (key, value) => {
    if (
      key === FORM_FIELDS.VESSEL_CONDITION &&
      !shouldShowCargoOnboardSection(value as string | null | undefined)
    ) {
      setField(
        FORM_FIELDS.VESSEL_CONDITION,
        value as VesselCallFormData[typeof FORM_FIELDS.VESSEL_CONDITION],
      );
      setField(FORM_FIELDS.CARGO_ONBOARD, []);
      return;
    }

    setField(
      key as keyof VesselCallFormData,
      value as VesselCallFormData[keyof VesselCallFormData],
    );
  };

  const clearStoreFile = () => {
    DOC_UPLOAD_FIELDS.forEach((file) => {
      uploadStore.clearFilesForKey(file);
    });
    uploadStore.clearFilesForKey(FORM_FIELDS.CALL_ADDITIONAL_DOCUMENTS);
  };

  const clearForm = () => {
    skipMissingVesselGuardRef.current = true;
    resetForm();
    clear();
    clearStoreFile();
    reset();
    setField(FORM_FIELDS.CALL_ADDITIONAL_DOCUMENTS, []);
  };

  const cancel = () => {
    clearForm();
    setField(FORM_FIELDS.CALL_ADDITIONAL_DOCUMENTS, []);
    navigate(`/${paths.vesselCall.index}`);
  };

  useEffect(() => {
    if (
      sourceCall &&
      [VESSEL_CALL_STATUSES.PRE_APPROVED, VESSEL_CALL_STATUSES.REJECTED_MARINE_OPERATOR].includes(
        sourceCall.status,
      ) &&
      shouldShowCertWarning === undefined
    ) {
      const missingOrExpiringCertificates = validateVesselCertificates(
        sourceCall.expectedArrivalLocation,
        vesselDetails,
        vesselDetails?.diplomaticVessel,
      );
      if (missingOrExpiringCertificates.length > 0) {
        setShouldShowCertWarning(true);
      }
    }
  }, [sourceCall, vesselDetails, shouldShowCertWarning, notify, t]);

  const { onSubmit, isPending } = useCreateVesselCall({
    resetForm: clearForm,
    existingCallId: sourceCall?.id ?? null,
    existingCallStatus: sourceCall?.status ?? null,
    vesselDetails,
  });

  const onValid = async () => {
    if (vesselDetails?.bunkerRegistered && isFinalSubmission) {
      const hasExpiredBunkerBargeCertificate =
        vesselDetails.bunkerBargeDetails?.certificates?.some((certificate) => {
          if (!certificate.expiryDate) return false;
          const certToDate = new Date(certificate.expiryDate);
          if (Number.isNaN(certToDate.getTime())) return false;

          certToDate.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          return certToDate < today;
        }) ?? false;

      if (hasExpiredBunkerBargeCertificate) {
        notify({
          title: t(`${LANG_BASE}.notifications.bunker-barge-not-valid`),
          type: "error",
        });
        return;
      }
    }

    await onSubmit();
    setField(FORM_FIELDS.CALL_ADDITIONAL_DOCUMENTS, []);
  };

  const onInvalid = () => {
    notify({
      title: t(`${LANG_BASE}.notifications.vessel-call-submitted-error-title`),
      description: t(`${LANG_BASE}.notifications.vessel-call-submitted-error-description`),
      type: "error",
    });
  };

  const notifyTemplateImportsPending = () => {
    notify({
      title: t("common.notification.please-wait-for-upload"),
      type: "warning",
    });
  };

  const handleSave = async () => {
    if (hasActiveNavigationBlockers) {
      notifyTemplateImportsPending();
      return;
    }

    syncPortCallsField();
    syncCrewField();

    clearErrors();

    const values = form.getValues();
    const parsed = await draftSchema.safeParseAsync(values);

    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        if (issue.path.length === 0) return;

        const fieldName = issue.path[0] as keyof VesselCallFormData;

        form.setValue(fieldName, form.getValues(fieldName), {
          shouldDirty: false,
          shouldTouch: true,
          shouldValidate: false,
        });

        setError(issue.path.join(".") as any, {
          type: "manual",
          message: issue.message,
        });
      });

      notify({
        title: t("vessel-call.create.notifications.vessel-call-draft-saved-error-title"),
        description: t(
          "vessel-call.create.notifications.vessel-call-draft-saved-error-description",
        ),
        type: "error",
      });

      return;
    }

    await onSubmit({ isDraft: true });
  };

  const syncPortCallsField = () => {
    const { portCalls } = useVesselCallFormStore.getState();
    form.setValue(FORM_FIELDS.PORT_CALLS, portCalls ?? [], {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  const syncCrewField = () => {
    const { crew } = useVesselCallFormStore.getState();
    form.setValue("crew", crew ?? [], {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  const complete = () => {
    if (hasActiveNavigationBlockers) {
      notifyTemplateImportsPending();
      return;
    }

    setTouchedSteps(
      Object.keys(STEP_FIELDS).reduce(
        (acc, key) => {
          acc[Number(key)] = true;
          return acc;
        },
        {} as Record<number, boolean>,
      ),
    );

    syncPortCallsField();
    syncCrewField();

    form.handleSubmit(onValid, onInvalid)();
  };

  const join = (...parts: string[]) => "/" + [index, ...parts].join("/");

  const flat = useMemo(() => {
    const items: { to: string; topIndex: number }[] = [];
    steps.forEach((s, topIndex) => {
      if (s.substeps?.length) {
        s.substeps.forEach((sub) => {
          items.push({ to: join(s.path, sub.path), topIndex });
        });
      } else {
        items.push({ to: join(s.path), topIndex });
      }
    });
    return items;
  }, []);

  const getCurrentSubstepIndex = () => {
    const currentStep = steps[currentStepIndex];
    if (!currentStep?.substeps?.length) return -1;

    const segments = location.pathname.split("/").filter(Boolean);
    const currentLeaf = segments[segments.length - 1];

    return currentStep.substeps.findIndex((substep) => substep.path === currentLeaf);
  };

  const getAdjacentStepperLabelKey = (direction: "back" | "next") => {
    const offset = direction === "next" ? 1 : -1;
    const currentStep = steps[currentStepIndex];
    if (!currentStep) return undefined;

    if (!currentStep.substeps?.length) {
      return steps[currentStepIndex + offset]?.label;
    }

    const currentSubstepIndex = getCurrentSubstepIndex();
    if (currentSubstepIndex === -1) {
      return steps[currentStepIndex + offset]?.label;
    }

    const adjacentSubstep = currentStep.substeps[currentSubstepIndex + offset];
    if (adjacentSubstep) {
      return adjacentSubstep.label;
    }

    return steps[currentStepIndex + offset]?.label;
  };

  const activeFlatIndex = useMemo(() => {
    const { pathname } = location;

    let i = flat.findIndex((x) => pathname.endsWith(x.to));
    if (i !== -1) return i;
    const topIdx = steps.findIndex((s) => pathname.includes(`/${s.path}`));
    if (topIdx > -1) {
      const first = flat.findIndex((x) => x.topIndex === topIdx);
      if (first > -1) return first;
    }
    return 0;
  }, [location.pathname, flat]);

  const currentStepIndex = useMemo(
    () => flat[activeFlatIndex]?.topIndex ?? 0,
    [activeFlatIndex, flat],
  );

  const { statusFor } = useStepStatuses<VCFormData>({
    schema,
    form,
    stepFields: STEP_FIELDS,
    touchedSteps,
    currentStepIndex,
  });

  const getStatusForStep = (index: number): StepStatusValue =>
    index === currentStepIndex ? StepStatus.CURRENT : statusFor(index);

  const isRejectedPrefill =
    !!prefillFromId &&
    [
      VESSEL_CALL_STATUSES.REJECTED_MARINE_OPERATOR,
      VESSEL_CALL_STATUSES.REJECTED_MARINE_SUPERVISOR,
    ].includes(
      sourceCall?.status as (typeof VESSEL_CALL_STATUSES)[keyof typeof VESSEL_CALL_STATUSES],
    );

  const shouldShowSaveDraft =
    isShippingAgentUser &&
    !isRejectedPrefill &&
    hasFormData(form.getValues(), emptyFormDefaults, [
      FORM_FIELDS.NAME_AND_TYPE_OF_SHIP,
      FORM_FIELDS.IMO_NUMBER,
      FORM_FIELDS.METHOD_AT_IMO_2020_COMPLIANCE,
      FORM_FIELDS.VESSEL_ID,
      FORM_FIELDS.PORT_FACILITY,
    ]);

  const goToFlat = (i: number) => {
    if (i < 0 || i >= flat.length || i === activeFlatIndex) return;

    const target = flat[i];

    requestNavigation(() => {
      setTouchedSteps((prev) => ({
        ...prev,
        [currentStepIndex]: true,
      }));
      navigatePreserve(target.to);
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const goPrev = () => {
    if (activeFlatIndex <= 0) return;
    goToFlat(activeFlatIndex - 1);
  };

  const goNext = () => {
    if (activeFlatIndex >= flat.length - 1) return;
    goToFlat(activeFlatIndex + 1);
  };

  const handleStepperTabChange = (nextIndex: number) => {
    if (nextIndex === currentStepIndex) return;

    const flatIndexForTab = flat.findIndex((item) => item.topIndex === nextIndex);
    if (flatIndexForTab === -1) return;

    return requestNavigation(() => {
      setTouchedSteps((prev) => ({
        ...prev,
        [currentStepIndex]: true,
      }));
      navigatePreserve(flat[flatIndexForTab].to);
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const outletCtx: VesselCallOutletContext = {
    formData: form,
    updateFormData,
    clearErrors,
    watch,
    setError,
    cancel,
    fetchedPorts,
    prefillFromId,
    isFinalSubmission,
    isRejectedFinalArrivalUpdate,
    shouldEnforceFinalValidations,
  };

  return (
    <PageLayout dir={dir}>
      <div className="pt-8 pb-8 px-24">
        {isPrefillHydrated && (
          <>
            <div className="mb-6">
              <VesselDetails
                testId={TEST_ID_BASE}
                langBase={`${LANG_BASE}.vessel-details`}
                vesselDetails={vesselDetails}
              />
            </div>
            <StepperTabs
              ref={stepperTabsRef}
              testId={`${TEST_ID_BASE}-stepper-tabs`}
              currentStepIndex={currentStepIndex}
              stepperType={STEPPER_TYPE.VERTICAL}
              isTypeChangeable={false}
              onStepperTabChange={handleStepperTabChange}
            >
              {steps.map((step, index) => (
                <StepperTabPanel
                  key={step.path}
                  label={t(`${step.label}`)}
                  status={getStatusForStep(index)}
                >
                  <div className="min-w-auto max-w-full flex flex-col flex-1">
                    <Outlet context={outletCtx} />
                    <div className="pb-6">
                      <StepperControls
                        prev={activeFlatIndex > 0 ? goPrev : undefined}
                        next={activeFlatIndex < flat.length - 1 ? goNext : undefined}
                        backLabelTranslationKey={getAdjacentStepperLabelKey("back")}
                        nextLabelTranslationKey={getAdjacentStepperLabelKey("next")}
                      />
                    </div>
                  </div>
                </StepperTabPanel>
              ))}
            </StepperTabs>
            <div className="py-6 border-t border-t-(--stroke-secondary-stroke,#E6EDF2)">
              <StepperControls
                cancel={cancel}
                save={shouldShowSaveDraft ? handleSave : undefined} // will remove condition when pre-arrival draft is implemented
                savePending={isPending}
                complete={complete}
                completePending={isPending}
                completeLabelTranslationKey="stepper-controls.submit"
              />
            </div>
          </>
        )}
      </div>
      <GenericDialog
        open={!!shouldShowCertWarning}
        title={t("vessel-call.create.notifications.certificates-please-check-title")}
        content={t("vessel-call.create.notifications.certificates-please-check")}
        cancelButtonText={t("common.close")}
        handleCloseDialog={() => setShouldShowCertWarning(false)}
      />
    </PageLayout>
  );
};

export default CreateVesselCallContainer;
